import * as api from '@codegrade/api';
import { Maybe } from '@codegrade/maybe';
import { assert, expr, object } from '@codegrade/utils';
import { getStoreBuilder } from '@codegrade/vuex-typex';
import client from '@/client';
import * as models from '@/models';
import * as utils from '@/utils';
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export class DecodeError extends Error {
    pref;
    value;
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    constructor(pref, value, decodeError) {
        super(`Invalid value for pref "${pref}": ${value}\n${decodeError}`);
        this.pref = pref;
        this.value = value;
    }
}
const storeBuilder = getStoreBuilder();
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
const makeInitialState = () => {
    let prefersDarkMode = false;
    if (window.matchMedia != null) {
        prefersDarkMode = window.matchMedia('(prefers-color-scheme: dark)').matches;
    }
    return {
        uiPrefs: {},
        loaders: {},
        prefersDarkMode,
        darkMode: prefersDarkMode,
    };
};
const moduleBuilder = storeBuilder.module('ui_prefs', makeInitialState());
/** The UI Preference store module. */
export var UIPrefsStore;
(function (UIPrefsStore) {
    /**
     * Get a UI preference from the state with a given name.
     *
     * @param state The state to retrieve the UI preference from.
     * @param preference The name of the preference to retrieve.
     * @param codec The codec that should be used to decode the found data, if
     * any was found.
     * @returns A tagged union that might contain the parsed data of the UI
     * preference.
     */
    function getUIPrefFromState(state, preference, codec) {
        return Maybe.fromNullable(state.uiPrefs[preference]).caseOf({
            Just: maybeValue => maybeValue.caseOf({
                Just: val => codec.decode(val).caseOf({
                    Left: message => {
                        const error = new DecodeError(preference, val, message);
                        return { status: 'not-valid', error };
                    },
                    Right: parsed => ({ status: 'valid', value: parsed }),
                }),
                Nothing: () => ({ status: 'not-present' }),
            }),
            Nothing: () => ({ status: 'not-loaded' }),
        });
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    UIPrefsStore.getUIPref = moduleBuilder.read(state => 
    /**
     * Get a UI preference from the current store state.
     *
     * @param pref The name of the preference to get.
     * @param codec The codec to parse the preference data with, if data was found.
     * @returns The same data as `getUIPrefFromState`.
     */
    (pref, codec) => getUIPrefFromState(state, pref, codec), 'getUIPref');
    /**
     * Retrieve if the user would prefer to use dark mode.
     *
     * @param state The state to use for retrieving the user preference and if
     * auto mode should use dark mode.
     * @returns True if the user would prefer dark mode, false if they want
     * light mode.
     */
    function getDarkMode(state) {
        const pref = getUIPrefFromState(state, 'theme', models.Theme.codec);
        return expr.match(pref, 'status', {
            'not-loaded': () => state.darkMode,
            'not-present': () => state.prefersDarkMode,
            'not-valid': () => state.prefersDarkMode,
            valid: validState => expr.match(validState, 'value', {
                auto: () => state.prefersDarkMode,
                dark: () => true,
                light: () => false,
            }),
        });
    }
    /** Does the user want to use dark mode. */
    UIPrefsStore.darkMode = moduleBuilder.read(getDarkMode, 'darkMode');
    /** Get the theme the user would prefer to use for a monaco editor. */
    UIPrefsStore.editorTheme = moduleBuilder.read((state) => (getDarkMode(state) ? 'stackoverflow-dark' : 'solarized-dark'), 'editorTheme');
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    UIPrefsStore.commitUIPrefs = moduleBuilder.commit((state, prefs) => {
        utils.vueSet(state, 'uiPrefs', Object.freeze({ ...state.uiPrefs, ...prefs }));
        // If a new theme was selected, we must set the value of darkMode so
        // that it gets persisted over reloads.
        if (object.hasAttr(prefs, 'theme')) {
            utils.vueSet(state, 'darkMode', getDarkMode(state));
        }
    }, 'commitUIPrefs');
    const commitPrefersDarkMode = moduleBuilder.commit((state, prefersDarkMode) => {
        state.prefersDarkMode = prefersDarkMode;
        state.darkMode = UIPrefsStore.darkMode();
    }, 'commitPrefersDarkMode');
    const addLoader = moduleBuilder.commit((state, { preference, loader }) => {
        utils.vueSet(state.loaders, preference, loader);
    }, 'addLoader');
    const removeLoader = moduleBuilder.commit((state, { preference }) => {
        utils.vueDelete(state.loaders, preference);
    }, 'removeLoader');
    const commitClear = moduleBuilder.commit(state => {
        state.uiPrefs = {};
        state.loaders = {};
    }, 'commitClear');
    /**
     * Load the value of the given preference for the current user.
     *
     * @param payload.preference The name of the preference to load.
     * @param client The client to perform the request with. If not given the
     * default client is used.
     * @returns A promise that resolves when a successful response is received,
     * or rejects when the request was not successful.
     */
    UIPrefsStore.loadUIPreference = moduleBuilder.dispatch(({ state }, payload) => {
        const c = payload.client ?? client;
        const preference = payload.preference;
        return utils.loadApiWithMaybeForce({
            value: Maybe.fromNullable(state.uiPrefs[preference]),
            loader: Maybe.fromNullable(state.loaders[preference]),
            force: false,
            load: async () => {
                const response = await c.p.userSetting.getUiPreference({
                    name: preference,
                });
                return response.map(cgResult => {
                    let value;
                    if (cgResult instanceof api.PresentPreference) {
                        value = Maybe.of(cgResult.value);
                    }
                    else if (cgResult instanceof api.NonPresentPreference) {
                        value = Maybe.empty();
                    }
                    else {
                        throw assert.AssertionError.assertNever(cgResult);
                    }
                    UIPrefsStore.commitUIPrefs({ [preference]: value });
                    return value;
                });
            },
            addLoader: loader => addLoader({ preference, loader }),
            removeLoader: () => removeLoader({ preference }),
        });
    }, 'loadUIPreference');
    /** Action to send path an UI Preference. */
    UIPrefsStore.patchUIPreference = moduleBuilder.dispatch((_, { preference, value }) => {
        UIPrefsStore.commitUIPrefs({ [preference]: Maybe.of(value) });
        return client.p.userSetting.patchUiPreference({
            requestBody: {
                name: preference,
                value,
            },
        });
    }, 'patchUIPreference');
    utils.signal.AFTER_USER_CHANGED.register(async (user) => {
        commitClear();
        if (user.isJust()) {
            utils.loadBackgroundPromises(UIPrefsStore.loadUIPreference({ preference: 'theme' }));
        }
    });
    if (window.matchMedia != null) {
        window
            .matchMedia('(prefers-color-scheme: dark)')
            .addEventListener('change', ({ matches }) => {
            commitPrefersDarkMode(matches);
        });
    }
})(UIPrefsStore || (UIPrefsStore = {}));
